import React, { useState, useEffect, useRef } from 'react';

interface AICoachAssistantProps {
  clientId: number | null;
  clientName: string | null;
  isMinimized: boolean;
  onToggleMinimize: () => void;
}

interface Message {
  role: 'user' | 'ai' | 'system';
  content: string;
  mode?: string;
}

type AIMode = 'coach' | 'insights' | 'planner';

export const AICoachAssistant: React.FC<AICoachAssistantProps> = ({
  clientId,
  clientName,
  isMinimized,
  onToggleMinimize,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState<AIMode>('coach');
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Initialize welcome message when client changes
  useEffect(() => {
    if (clientId && clientName) {
      setMessages([
        {
          role: 'system',
          content: `AI Coach Assistant ready for ${clientName}. Ask me about their nutrition patterns, progress, or get recommendations.`,
        },
      ]);
    } else {
      setMessages([
        {
          role: 'system',
          content: 'Select a client to start AI coaching assistance.',
        },
      ]);
    }
  }, [clientId, clientName]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || !clientId || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Add user message to chat
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}/api/coach/ai-chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage,
          mode: selectedMode,
          clientId: clientId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessages((prev) => [
          ...prev,
          { role: 'ai', content: data.message, mode: selectedMode },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: 'system', content: `Error: ${data.error || 'Failed to get AI response'}` },
        ]);
      }
    } catch (error) {
      console.error('AI chat error:', error);
      setMessages((prev) => [
        ...prev,
        { role: 'system', content: 'Network error. Please try again.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    'What patterns do you see?',
    'Any red flags?',
    'Should I set new targets?',
    'Meal recommendations?',
  ];

  if (isMinimized) {
    return null; // Component is hidden when minimized
  }

  return (
    <div className="h-full flex flex-col bg-white border-l border-gray-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4 flex justify-between items-center">
        <div>
          <h3 className="text-white font-semibold text-lg flex items-center gap-2">
            🤖 AI Coach Assistant
          </h3>
          <p className="text-purple-100 text-xs">
            {clientName || 'No client selected'}
          </p>
        </div>
        <button
          onClick={onToggleMinimize}
          className="text-white hover:bg-purple-800 p-2 rounded transition-colors"
          title="Minimize AI Assistant"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Mode Selector */}
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <div className="flex gap-2">
          {(['coach', 'insights', 'planner'] as AIMode[]).map((mode) => (
            <button
              key={mode}
              onClick={() => setSelectedMode(mode)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                selectedMode === mode
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'
              }`}
            >
              {mode === 'coach' && '💬'}
              {mode === 'insights' && '📊'}
              {mode === 'planner' && '📅'}
              {' '}
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Quick Questions */}
      {messages.length <= 1 && clientId && (
        <div className="p-3 border-b border-gray-200 bg-blue-50">
          <p className="text-xs text-gray-600 mb-2 font-medium">Quick questions:</p>
          <div className="flex flex-wrap gap-2">
            {quickQuestions.map((question, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setInputMessage(question);
                  setTimeout(() => sendMessage(), 100);
                }}
                className="px-2 py-1 bg-white border border-blue-200 rounded text-xs text-blue-700 hover:bg-blue-100 transition-colors"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chat Messages */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
      >
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`${
              msg.role === 'user'
                ? 'ml-8 bg-purple-600 text-white'
                : msg.role === 'ai'
                ? 'mr-8 bg-white border border-gray-200'
                : 'bg-blue-50 border border-blue-200 text-center text-sm'
            } rounded-lg p-3`}
          >
            {msg.role === 'ai' && (
              <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                {msg.mode === 'coach' && '💬 Coach'}
                {msg.mode === 'insights' && '📊 Insights'}
                {msg.mode === 'planner' && '📅 Planner'}
              </div>
            )}
            <div
              className={`text-sm ${msg.role === 'user' ? 'text-white' : 'text-gray-800'}`}
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="mr-8 bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
              AI is thinking...
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={
              clientId
                ? 'Ask me about nutrition patterns, targets, or recommendations...'
                : 'Select a client first...'
            }
            disabled={!clientId || isLoading}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <button
            onClick={sendMessage}
            disabled={!clientId || !inputMessage.trim() || isLoading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
